import { config } from '~app/config';

import { ampli } from '~entities/ampli';

import { makeRequest } from '~shared/api/make-request';

import { ResponseSuccess } from '@breezeventures/scp-client';
import { calculateStringHash } from '@breezeventures/utils/calculate-string-hash';
import { getAuthMetadata } from '@breezeventures/utils/get-metadata';
import {
  AuthApi,
  ValidateResponse,
  OtpResponse,
  AuthSuccessResponse,
  isOtpResponse,
  AuthErrorResponse,
  isAuthErrorResponse,
} from '@breezeventures/web-ph-client-auth/api';

export class GatewayAuthApi implements AuthApi {
  private clientId: string;
  private apiHost: string;
  private captchaLogEvent: (event: string) => void;
  inactivityTimeoutMs = 60 * 60 * 1000;

  private session: string | null;
  private username: string | null;

  constructor(
    clientId: string,
    apiHost: string,
    captchaLogEvent: (event: string) => void
  ) {
    this.clientId = clientId;
    this.session = null;
    this.username = null;
    this.apiHost = apiHost;
    this.captchaLogEvent = captchaLogEvent;
  }

  async getMetadata() {
    const metadata = await getAuthMetadata();

    return {
      userMetadata: {
        ...metadata.clientMetadata,
        webFingerprint: {
          ...metadata.clientMetadata.webFingerprint,
          referer:
            config.environment === 'local'
              ? undefined
              : metadata.clientMetadata.webFingerprint.referer,
        },
      },
    };
  }

  async validate() {
    return makeRequest<ResponseSuccess<ValidateResponse>>({
      url: `${this.apiHost}/auth/api/v1/merchant_authorizer/tokens/validate`,
      credentials: 'include',
    }).then((response) => response.payload);
  }

  async initiate(phoneNumber: string) {
    this.session = null;
    this.username = null;

    try {
      const metadata = await this.getMetadata();
      const clientInfoHash = await calculateStringHash(
        this.clientId +
          phoneNumber +
          metadata.userMetadata.webFingerprint.webUID
      );
      const response = await makeRequest<ResponseSuccess<OtpResponse>>({
        url: `${this.apiHost}/auth/api/v1/merchant_authorizer/challenge_flow/initiate`,
        headers: {
          'X-Client-Info': clientInfoHash,
        },
        payload: {
          clientId: this.clientId,
          authParameters: { phoneNumber },
          ...metadata,
        },
        logEvent: this.captchaLogEvent,
      }).then((response) => response.payload);

      this.session = response.session;
      this.username = response.challengeParameters.username;

      return response;
    } catch (_err) {
      return {
        error: 'UNAUTHORIZED',
      } as AuthErrorResponse;
    }
  }

  async respond(answer: string) {
    if (!this.session || !this.username) {
      return {
        error: 'CHALLENGE_SESSION_EXPIRED',
      } as AuthErrorResponse;
    }

    try {
      const metadata = await this.getMetadata();
      const response = await makeRequest<
        ResponseSuccess<OtpResponse | AuthSuccessResponse>
      >({
        url: `${this.apiHost}/auth/api/v1/merchant_authorizer/challenge_flow/respond`,
        credentials: 'include',
        payload: {
          clientId: this.clientId,
          session: this.session,
          challengeResponses: {
            answer,
            username: this.username,
          },
          ...metadata,
        },
      }).then((response) => response.payload);

      if (
        isAuthErrorResponse(response) &&
        response.error === 'CHALLENGE_SESSION_EXPIRED'
      ) {
        ampli.client.track('mp_auth_api_otp_expired');
      }

      if (isOtpResponse(response)) {
        this.session = response.session;
        this.username = response.challengeParameters.username;
        return {
          error: 'CHALLENGE_CONFIRMATION_ERROR',
        } as AuthErrorResponse;
      }

      this.session = null;
      this.username = null;

      return response;
    } catch (_err) {
      return {
        error: 'UNAUTHORIZED',
      } as AuthErrorResponse;
    }
  }

  async resendConfirmation() {
    if (!this.session || !this.username) {
      return {
        error: 'CHALLENGE_SESSION_EXPIRED',
      } as AuthErrorResponse;
    }

    try {
      const response = await makeRequest<ResponseSuccess<OtpResponse>>({
        url: `${this.apiHost}/auth/api/v1/merchant_authorizer/challenge_flow/respond`,
        credentials: 'include',
        payload: {
          clientId: this.clientId,
          session: this.session,
          challengeResponses: {
            repeatChallenge: true,
            username: this.username,
          },
        },
      }).then((response) => response.payload);

      this.session = response.session;
      this.username = response.challengeParameters.username;

      return response;
    } catch (_err) {
      return {
        error: 'UNAUTHORIZED',
      } as AuthErrorResponse;
    }
  }

  async revoke() {
    try {
      const response = await makeRequest<ResponseSuccess<void>>({
        url: `${this.apiHost}/auth/api/v1/merchant_authorizer/challenge_flow/revoke`,
        credentials: 'include',
        payload: {
          clientId: this.clientId,
        },
      }).then((response) => response.payload);

      return response;
    } finally {
      this.session = null;
      this.username = null;
    }
  }
}
